import React from 'react';
import { ChevronDown } from 'lucide-react';
import { SortDirection } from '../../hooks/sorting/types';
import './Table.scss';
import { FilterableTableHeader } from '../FilterableTableHeader/FilterableTableHeader';
import { isCellTruncated, readTruncatedText, useTooltip } from '../Tooltip/Tooltip';
import { useIsMobile } from '../../hooks/useIsMobile';

interface Column<T> {
  id: string;
  header: string;
  accessor?: keyof T | ((row: T) => React.ReactNode);
  render?: (row: T) => React.ReactNode;
  className?: string;
  sortable?: boolean;
  sortKey?: string;
  /** Plain-language explanation of the column, shown when its header is hovered. */
  description?: string;
  /**
   * Tooltip for this column's cells. Return null for no tooltip.
   *
   * Without this, a cell falls back to showing its own text, but only when that
   * text is visually clipped. Use this when the tooltip should say more than the
   * cell shows — a full address behind a city, a timestamp behind a date.
   */
  tooltip?: (row: T) => React.ReactNode;
  /** Initial width used when no saved width exists. */
  defaultWidth?: number;

  /** Smallest width allowed when resizing. */
  minWidth?: number;

  /** Largest width allowed when resizing. */
  maxWidth?: number;

  /** Disable resizing for this column. */
  resizable?: boolean;
}

interface TableSelection<T> {
  isSelected: (id: string) => boolean;
  onToggleRow: (row: T) => void;
  /** Selects every row on the page, or clears them when all are already selected. */
  onTogglePage: () => void;
  allPageSelected: boolean;
  somePageSelected: boolean;
  /** Describes a row in the checkbox's accessible label, e.g. "load D0042". */
  rowLabel?: (row: T) => string;
}

/**
 * Turns the table into a card list on phone-sized screens.
 *
 * A wide table stacked into one row per cell produces a card tall enough to
 * need its own scroll, so instead each row collapses to a heading, a handful of
 * promoted fields, and everything else behind a disclosure. Columns the user
 * hid in the ColumnManager never reach here, and the ones they kept are all
 * still reachable — just not all at once.
 */
interface TableMobileCard {
  /** Column shown as the card heading, e.g. the load number. */
  titleColumnId: string;
  /** Column pinned to the right of the heading, e.g. the status pill. */
  trailingColumnId?: string;
  /** Columns always shown under the heading, in this order. */
  primaryColumnIds?: string[];
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  isLoading?: boolean;
  emptyMessage?: string;
  className?: string;
  sortBy?: string | null;
  sortDirection?: SortDirection;
  onSort?: (columnId: string) => void;
  getRowClassName?: (row: T) => string;
  tableId?: string;
  selection?: TableSelection<T>;
  /** Opt in to the mobile card layout. Without it the table renders as-is at every width. */
  mobileCard?: TableMobileCard;
}

type ColumnWidths = Record<string, number>;

function readStoredColumnWidths(storageKey: string): ColumnWidths {
  try {
    const stored = localStorage.getItem(storageKey);

    if (!stored) {
      return {};
    }

    const parsed = JSON.parse(stored);

    if (!parsed || typeof parsed !== 'object') {
      return {};
    }

    return parsed;
  } catch {
    return {};
  }
}

function usePersistedColumnWidths(tableId?: string) {
  const storageKey = tableId ? `table-column-widths:${tableId}` : null;

  const [columnWidths, setColumnWidths] = React.useState<ColumnWidths>(() => {
    if (!storageKey || typeof window === 'undefined') {
      return {};
    }

    return readStoredColumnWidths(storageKey);
  });

  React.useEffect(() => {
    if (!storageKey) {
      return;
    }

    try {
      localStorage.setItem(storageKey, JSON.stringify(columnWidths));
    } catch {
      // Ignore storage failures, such as private browsing restrictions.
    }
  }, [columnWidths, storageKey]);

  const setColumnWidth = React.useCallback((columnId: string, width: number) => {
    setColumnWidths((current) => ({
      ...current,
      [columnId]: Math.round(width),
    }));
  }, []);

  const resetColumnWidths = React.useCallback(() => {
    setColumnWidths({});

    if (storageKey) {
      localStorage.removeItem(storageKey);
    }
  }, [storageKey]);

  return {
    columnWidths,
    setColumnWidth,
    resetColumnWidths,
  };
}

export function Table<T extends { id: string | number }>({
  columns,
  data,
  isLoading = false,
  emptyMessage = 'No data available',
  className = '',
  sortBy = null,
  sortDirection = 'asc',
  onSort,
  getRowClassName,
  tableId,
  selection,
  mobileCard,
}: TableProps<T>) {
  // One tooltip shared by every header and cell in this table.
  const { show, hide, tooltip } = useTooltip();

  const isMobile = useIsMobile();
  const [expandedRows, setExpandedRows] = React.useState<Record<string, boolean>>({});

  const selectAllRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = Boolean(selection?.somePageSelected);
    }
  }, [selection?.somePageSelected]);

  const { columnWidths, setColumnWidth } = usePersistedColumnWidths(tableId);

  const handleCellEnter = (col: Column<T>, row: T) => (event: React.MouseEvent<HTMLElement>) => {
    const cell = event.currentTarget;

    if (col.tooltip) {
      show(col.tooltip(row), cell);
      return;
    }

    // No explicit tooltip: only speak up when the cell is actually clipped,
    // otherwise every cell in the table would sprout a redundant bubble.
    if (isCellTruncated(cell)) {
      show(readTruncatedText(cell), cell);
    }
  };

  const getColumnWidth = React.useCallback(
    (column: Column<T>) => {
      return columnWidths[column.id] ?? column.defaultWidth;
    },
    [columnWidths]
  );

  if (isLoading) {
    return <div className="skeleton" style={{ height: 220 }} />;
  }

  if (data.length === 0) {
    return (
      <div className="card card--bleed-top" style={{ padding: 16 }}>
        <div className="muted">{emptyMessage}</div>
      </div>
    );
  }

  const getCellContent = (col: Column<T>, row: T): React.ReactNode => {
    if (col.render) return col.render(row);

    if (col.accessor) {
      if (typeof col.accessor === 'function') return col.accessor(row);

      const value = row[col.accessor];

      if (value === null || value === undefined) return null;
      if (typeof value === 'string' || typeof value === 'number') return value;

      return String(value);
    }

    return null;
  };

  if (isMobile && mobileCard) {
    const byId = new Map(columns.map((col) => [col.id, col]));

    const titleColumn = byId.get(mobileCard.titleColumnId);
    const trailingColumn = mobileCard.trailingColumnId
      ? byId.get(mobileCard.trailingColumnId)
      : undefined;

    const primaryColumns = (mobileCard.primaryColumnIds ?? [])
      .map((id) => byId.get(id))
      .filter((col): col is Column<T> => Boolean(col));

    // Anything promoted to the heading or the always-visible block must not be
    // repeated in the disclosure.
    const promoted = new Set(
      [titleColumn?.id, trailingColumn?.id, ...primaryColumns.map((col) => col.id)].filter(
        Boolean
      ) as string[]
    );
    const detailColumns = columns.filter((col) => !promoted.has(col.id));

    const renderFields = (row: T, fieldColumns: Column<T>[], modifier?: string) => (
      <dl className={`table-card__fields${modifier ? ` ${modifier}` : ''}`}>
        {fieldColumns.map((col) => (
          <div className="table-card__field" key={col.id}>
            <dt>{col.header}</dt>
            <dd>{getCellContent(col, row)}</dd>
          </div>
        ))}
      </dl>
    );

    return (
      <div className={`card card--bleed-top table-cards ${className}`}>
        {selection && (
          <div className="table-cards__bar">
            <label className="table-cards__select-all">
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={selection.allPageSelected}
                onChange={selection.onTogglePage}
              />
              {selection.allPageSelected ? 'Clear selection' : 'Select all on page'}
            </label>
          </div>
        )}

        <ul className="table-cards__list">
          {data.map((row) => {
            const rowId = String(row.id);
            const isRowSelected = Boolean(selection?.isSelected(rowId));
            const isExpanded = Boolean(expandedRows[rowId]);

            return (
              <li
                key={row.id}
                className={[
                  'table-card',
                  getRowClassName ? getRowClassName(row) : '',
                  isRowSelected ? 'table-card--selected' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className="table-card__head">
                  {selection && (
                    <input
                      type="checkbox"
                      className="table-card__select"
                      checked={isRowSelected}
                      onChange={() => selection.onToggleRow(row)}
                      aria-label={`${isRowSelected ? 'Deselect' : 'Select'} ${
                        selection.rowLabel?.(row) ?? 'row'
                      }`}
                    />
                  )}

                  {titleColumn && (
                    <div className="table-card__title">{getCellContent(titleColumn, row)}</div>
                  )}

                  {trailingColumn && (
                    <div className="table-card__trailing">
                      {getCellContent(trailingColumn, row)}
                    </div>
                  )}
                </div>

                {primaryColumns.length > 0 && renderFields(row, primaryColumns)}

                {detailColumns.length > 0 && (
                  <>
                    <button
                      type="button"
                      className="table-card__toggle"
                      aria-expanded={isExpanded}
                      onClick={() =>
                        setExpandedRows((current) => ({ ...current, [rowId]: !current[rowId] }))
                      }
                    >
                      <ChevronDown
                        size={14}
                        className={`table-card__chevron${
                          isExpanded ? ' table-card__chevron--open' : ''
                        }`}
                      />
                      {isExpanded
                        ? 'Hide details'
                        : `${detailColumns.length} more ${
                            detailColumns.length === 1 ? 'field' : 'fields'
                          }`}
                    </button>

                    {isExpanded && renderFields(row, detailColumns, 'table-card__fields--details')}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  return (
    <div className="card card--bleed-top">
      <div className="table-scroll">
        <table className={`table table--resizable ${className}`}>
          <colgroup>
            {selection && (
              <col
                className="table__select-col"
                style={{
                  width: '44px',
                  minWidth: '44px',
                  maxWidth: '44px',
                }}
              />
            )}

            {columns.map((column) => {
              const width = getColumnWidth(column);

              return (
                <col
                  key={column.id}
                  style={
                    width != null
                      ? {
                          width: `${width}px`,
                          minWidth: `${width}px`,
                        }
                      : undefined
                  }
                />
              );
            })}
          </colgroup>

          <thead>
            <tr>
              {selection && (
                <th className="table__select-cell">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    checked={selection.allPageSelected}
                    onChange={selection.onTogglePage}
                    aria-label={
                      selection.allPageSelected
                        ? 'Clear selection on this page'
                        : 'Select all rows on this page'
                    }
                  />
                </th>
              )}
              {columns.map((col) => {
                const columnId = col.id;
                const isSorted = sortBy === columnId || sortBy === col.sortKey;
                const canSort = Boolean(col.sortable && onSort);

                return (
                  <FilterableTableHeader
                    key={columnId}
                    columnId={col.sortKey || columnId}
                    resizeColumnId={columnId}
                    header={col.header}
                    sortable={canSort}
                    isSorted={isSorted}
                    sortDirection={sortDirection}
                    onSort={onSort}
                    className={col.className}
                    description={col.description}
                    onShowTooltip={show}
                    onHideTooltip={hide}
                    width={getColumnWidth(col)}
                    minWidth={col.minWidth ?? 80}
                    maxWidth={col.maxWidth ?? 600}
                    resizable={col.resizable !== false}
                    onResize={setColumnWidth}
                  />
                );
              })}
            </tr>
          </thead>

          <tbody>
            {data.map((row) => {
              const rowId = String(row.id);
              const isRowSelected = Boolean(selection?.isSelected(rowId));

              return (
                <tr
                  key={row.id}
                  className={[
                    getRowClassName ? getRowClassName(row) : '',
                    isRowSelected ? 'table__row--selected' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {selection && (
                    <td className="table__select-cell">
                      <input
                        type="checkbox"
                        checked={isRowSelected}
                        onChange={() => selection.onToggleRow(row)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`${isRowSelected ? 'Deselect' : 'Select'} ${
                          selection.rowLabel?.(row) ?? 'row'
                        }`}
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td
                      key={col.id}
                      className={col.className}
                      onMouseEnter={handleCellEnter(col, row)}
                      onMouseLeave={hide}
                    >
                      {getCellContent(col, row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {tooltip}
    </div>
  );
}
